import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import {
  Text,
  TextInput,
  Button,
  useTheme,
  Card,
  Checkbox,
  Divider,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import { selectUserProfile } from '../store/slices/userSlice';
import { AlertTriangle, Trash2, ArrowLeft } from 'lucide-react-native';
import Toast from 'react-native-toast-message';
import { useLogout } from '../hooks/useLogout';

export function DeleteAccountScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { logout } = useLogout();

  // User state
  const profile = useAppSelector(selectUserProfile);

  // Local state
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [acknowledgeDataLoss, setAcknowledgeDataLoss] = useState(false);
  const [acknowledgeNoRecovery, setAcknowledgeNoRecovery] = useState(false);
  const [acknowledgeSubscription, setAcknowledgeSubscription] = useState(false);

  const requiredConfirmationText = 'DELETE MY ACCOUNT';
  const isFormValid =
    password.trim().length > 0 &&
    confirmationText === requiredConfirmationText &&
    acknowledgeDataLoss &&
    acknowledgeNoRecovery &&
    acknowledgeSubscription;

  const handleDeleteAccount = async () => {
    if (!isFormValid) {
      Toast.show({
        type: 'error',
        text1: 'Please complete all required fields',
      });
      return;
    }

    Alert.alert(
      'Final Confirmation',
      'This is your last chance to cancel. Are you absolutely sure you want to delete your account? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete Forever',
          style: 'destructive',
          onPress: performAccountDeletion,
        },
      ]
    );
  };

  const performAccountDeletion = async () => {
    try {
      setIsDeleting(true);

      // Call API to delete account
      const response = await fetch('/api/users/delete-account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password,
          confirmation: confirmationText,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete account');
      }

      Toast.show({
        type: 'success',
        text1: 'Account deleted successfully',
        text2: "We're sorry to see you go",
      });

      // Log out and redirect to auth
      await logout();
    } catch (error: any) {
      Toast.show({
        type: 'error',
        text1: 'Failed to delete account',
        text2: error.message || 'Please try again later',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (
      password ||
      confirmationText ||
      acknowledgeDataLoss ||
      acknowledgeNoRecovery ||
      acknowledgeSubscription
    ) {
      Alert.alert(
        'Discard Changes?',
        'Are you sure you want to go back? Your progress will be lost.',
        [
          { text: 'Keep Editing', style: 'cancel' },
          { text: 'Discard', onPress: () => navigation.goBack() },
        ]
      );
    } else {
      navigation.goBack();
    }
  };

  const dataLossItems = [
    'All your recipes and brewing guides',
    'Your profile information and avatar',
    'Favorites and bookmarked recipes',
    'Following/followers relationships',
    'Comments and recipe ratings',
    'AI preferences and recommendations',
    'Brewing history and statistics',
    'Any premium subscription benefits',
  ];

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <Button
            mode='text'
            icon={ArrowLeft}
            onPress={handleCancel}
            textColor={theme.colors.onSurface}
          >
            Cancel
          </Button>
          <Text variant='headlineSmall' style={styles.headerTitle}>
            Delete Account
          </Text>
          <View style={{ width: 80 }} />
        </View>

        {/* Warning Card */}
        <Card
          style={[
            styles.warningCard,
            { backgroundColor: theme.colors.errorContainer },
          ]}
        >
          <Card.Content style={styles.warningContent}>
            <AlertTriangle size={32} color={theme.colors.error} />
            <View style={styles.warningText}>
              <Text variant='titleMedium' style={{ color: theme.colors.error }}>
                This action cannot be undone
              </Text>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onErrorContainer }}
              >
                Deleting your account will permanently remove all your data from
                our servers.
              </Text>
            </View>
          </Card.Content>
        </Card>

        {/* Data Loss Information */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              What will be deleted:
            </Text>
            {dataLossItems.map((item, index) => (
              <View key={index} style={styles.dataLossItem}>
                <Text variant='bodyMedium' style={styles.bulletPoint}>
                  •
                </Text>
                <Text variant='bodyMedium' style={styles.dataLossText}>
                  {item}
                </Text>
              </View>
            ))}
          </Card.Content>
        </Card>

        {/* Confirmation Form */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Confirm Account Deletion
            </Text>

            {/* Password Input */}
            <View style={styles.inputContainer}>
              <TextInput
                label='Enter your password'
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                mode='outlined'
                style={styles.input}
                error={password.length > 0 && password.length < 8}
                disabled={isDeleting}
              />
              {password.length > 0 && password.length < 8 && (
                <Text variant='bodySmall' style={styles.errorText}>
                  Password must be at least 8 characters
                </Text>
              )}
            </View>

            {/* Confirmation Text */}
            <View style={styles.inputContainer}>
              <Text variant='bodyMedium' style={styles.confirmationLabel}>
                Type "{requiredConfirmationText}" to confirm:
              </Text>
              <TextInput
                value={confirmationText}
                onChangeText={setConfirmationText}
                mode='outlined'
                style={styles.input}
                error={
                  confirmationText.length > 0 &&
                  confirmationText !== requiredConfirmationText
                }
                disabled={isDeleting}
                placeholder={requiredConfirmationText}
              />
              {confirmationText.length > 0 &&
                confirmationText !== requiredConfirmationText && (
                  <Text variant='bodySmall' style={styles.errorText}>
                    Text must match exactly
                  </Text>
                )}
            </View>

            <Divider style={styles.divider} />

            {/* Acknowledgment Checkboxes */}
            <Text variant='titleSmall' style={styles.acknowledgmentTitle}>
              Please acknowledge the following:
            </Text>

            <View style={styles.checkboxContainer}>
              <Checkbox
                status={acknowledgeDataLoss ? 'checked' : 'unchecked'}
                onPress={() => setAcknowledgeDataLoss(!acknowledgeDataLoss)}
                disabled={isDeleting}
              />
              <Text variant='bodyMedium' style={styles.checkboxLabel}>
                I understand that all my data will be permanently deleted and
                cannot be recovered.
              </Text>
            </View>

            <View style={styles.checkboxContainer}>
              <Checkbox
                status={acknowledgeNoRecovery ? 'checked' : 'unchecked'}
                onPress={() => setAcknowledgeNoRecovery(!acknowledgeNoRecovery)}
                disabled={isDeleting}
              />
              <Text variant='bodyMedium' style={styles.checkboxLabel}>
                I understand that CoffeePlexity cannot restore my account or
                data after deletion.
              </Text>
            </View>

            <View style={styles.checkboxContainer}>
              <Checkbox
                status={acknowledgeSubscription ? 'checked' : 'unchecked'}
                onPress={() =>
                  setAcknowledgeSubscription(!acknowledgeSubscription)
                }
                disabled={isDeleting}
              />
              <Text variant='bodyMedium' style={styles.checkboxLabel}>
                I understand that any active subscriptions will be cancelled and
                are non-refundable.
              </Text>
            </View>
          </Card.Content>
        </Card>

        {/* Alternative Options */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Consider these alternatives:
            </Text>

            <Button
              mode='outlined'
              onPress={() => navigation.navigate('privacySecurity' as never)}
              style={styles.alternativeButton}
            >
              Update Privacy Settings
            </Button>

            <Button
              mode='outlined'
              onPress={() => navigation.navigate('settings' as never)}
              style={styles.alternativeButton}
            >
              Adjust Notification Settings
            </Button>

            <Button
              mode='outlined'
              onPress={() => {
                Toast.show({
                  type: 'info',
                  text1: 'Contact Support',
                  text2: 'Redirecting to support...',
                });
                navigation.navigate('helpSupport' as never);
              }}
              style={styles.alternativeButton}
            >
              Contact Support Instead
            </Button>
          </Card.Content>
        </Card>

        {/* Delete Button */}
        <Button
          mode='contained'
          onPress={handleDeleteAccount}
          disabled={!isFormValid || isDeleting}
          loading={isDeleting}
          icon={Trash2}
          style={[styles.deleteButton, { backgroundColor: theme.colors.error }]}
          textColor={theme.colors.onError}
        >
          {isDeleting ? 'Deleting Account...' : 'Delete My Account Forever'}
        </Button>

        {/* Footer Note */}
        <Text variant='bodySmall' style={styles.footerNote}>
          Need help? Contact our support team at support@coffeeplexity.com
          before deleting your account.
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  headerTitle: {
    fontWeight: '600',
  },
  warningCard: {
    marginBottom: 16,
    elevation: 2,
  },
  warningContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  warningText: {
    flex: 1,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  dataLossItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  bulletPoint: {
    marginRight: 8,
    fontWeight: 'bold',
  },
  dataLossText: {
    flex: 1,
  },
  inputContainer: {
    marginBottom: 16,
  },
  input: {
    backgroundColor: 'transparent',
  },
  confirmationLabel: {
    marginBottom: 8,
    fontWeight: '500',
  },
  errorText: {
    color: '#d32f2f',
    marginTop: 4,
    marginLeft: 12,
  },
  divider: {
    marginVertical: 16,
  },
  acknowledgmentTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    paddingRight: 16,
  },
  checkboxLabel: {
    flex: 1,
    marginLeft: 8,
    lineHeight: 20,
  },
  alternativeButton: {
    marginBottom: 8,
  },
  deleteButton: {
    marginVertical: 24,
    paddingVertical: 8,
  },
  footerNote: {
    textAlign: 'center',
    opacity: 0.7,
    fontStyle: 'italic',
  },
});
